import { Field, GraphConfiguration } from "@/type/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { useContext, useEffect, useState } from "react";
import FieldSelector from "@/components/ui/field-selector";
import { Label } from "@/components/ui/label";
import { useGraphConfig } from "@/store/contexts/GraphConfigContext";
import DomainDatePicker from "./Domain/DatePicker";
import ManyToOnePicker from "./Domain/ManyToOnePicker";
import ManyToManyPicker from "./Domain/ManyToManyPicker";
import axios from "@/services/axios";
import { refactor_fields } from "@/utils/refactor";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";
import { useTranslation } from "react-i18next";

// Comparison types for Odoo domains
type ComparisonType =
  | "=" // equal to
  | "!=" // not equal to
  | "in" // is in
  | "not in" // is not in
  | "like" // includes
  | "not like" // does not include
  | ">" // greater than
  | ">=" // greater than or equal to
  | "<" // less than
  | "<=" // less than or equal to
  | "is_set" // is set/defined (will be converted to != false)
  | "is_not_set"; // is not set/defined (will be converted to = false)

// Interface for a single domain rule
export interface DomainRule {
  id: string; // Unique identifier for the rule
  field: string; // Field path (can be nested like partner_id.name)
  fieldObject: Field | null; // Complete field object with metadata
  operator: ComparisonType; // Comparison operator
  value: any; // Value to compare with (can be string, number, boolean, or array)
}

// Mapping of user-friendly operator labels to actual Odoo operators
const getOperatorMappings = (t: any) => [
  { label: t("editor.graphConfig.domain.operators.equal"), value: "=" },
  { label: t("editor.graphConfig.domain.operators.notEqual"), value: "!=" },
  { label: t("editor.graphConfig.domain.operators.in"), value: "in" },
  { label: t("editor.graphConfig.domain.operators.notIn"), value: "not in" },
  { label: t("editor.graphConfig.domain.operators.includes"), value: "like" },
  {
    label: t("editor.graphConfig.domain.operators.doesNotInclude"),
    value: "not like",
  },
  { label: t("editor.graphConfig.domain.operators.isSet"), value: "is_set" },
  {
    label: t("editor.graphConfig.domain.operators.isNotSet"),
    value: "is_not_set",
  },
  { label: t("editor.graphConfig.domain.operators.greaterThan"), value: ">" },
  {
    label: t("editor.graphConfig.domain.operators.greaterThanOrEqual"),
    value: ">=",
  },
  { label: t("editor.graphConfig.domain.operators.lessThan"), value: "<" },
  {
    label: t("editor.graphConfig.domain.operators.lessThanOrEqual"),
    value: "<=",
  },
];

// Convert DomainRules to Odoo domain format: [field, operator, value]
const convertDomainRulesToOdooFormat = (
  domainRules: DomainRule[],
  fields: Field[]
): Array<[string, string, any]> => {
  return domainRules.map((rule) => {
    // Special case handling for "is set" and "is not set"
    if (rule.operator === "is_set") {
      return [rule.field, "!=", false];
    } else if (rule.operator === "is_not_set") {
      return [rule.field, "=", false];
    }

    // Use fieldObject if available (for nested fields), otherwise find in root fields
    const fieldInfo =
      rule.fieldObject || fields.find((f) => f.field === rule.field);
    let value = rule.value;

    // Handle list values for "in" and "not in"
    if (rule.operator === "in" || rule.operator === "not in") {
      try {
        // Try to parse as JSON if it's a string representation of array
        let parsedValue;
        if (typeof value === "string" && value.trim().startsWith("[")) {
          parsedValue = JSON.parse(value);
        } else if (typeof value === "string") {
          parsedValue = value.split(",").map((v) => {
            const trimmed = v.trim();
            // Convert each value in array based on field type
            if (["integer", "many2one"].includes(fieldInfo?.type as any)) {
              return parseInt(trimmed, 10);
            } else if (fieldInfo?.type === "float") {
              return parseFloat(trimmed);
            } else {
              return trimmed;
            }
          });
        } else {
          parsedValue = value;
        }

        return [
          rule.field,
          rule.operator,
          Array.isArray(parsedValue) ? parsedValue : [parsedValue],
        ];
      } catch (e) {
        // If parsing fails, treat as a single value in an array
        return [rule.field, rule.operator, [value]];
      }
    } else {
      if (fieldInfo) {
        if (["integer", "many2one"].includes(fieldInfo.type) && value !== "") {
          if (
            typeof value === "string" &&
            !value.includes("@@") &&
            !value.includes("!!")
          ) {
            value = parseInt(value, 10);
          }
        } else if (
          ["float", "monetary"].includes(fieldInfo.type) &&
          value !== ""
        ) {
          value = parseFloat(value);
        } else if (fieldInfo.type === "boolean") {
          value = value === true || value === "true";
        }
      }
    }

    // Return regular format
    return [rule.field, rule.operator, value];
  });
};

// Convert Odoo domain format to DomainRules
const convertOdooFormatToDomainRules = (
  domain: Array<[string, string, any]> = []
): DomainRule[] => {
  return domain.map(([field, operator, value]) => {
    // Handle special cases
    if (operator === "!=" && value === false) {
      return {
        id: uuidv4(),
        field,
        fieldObject: null, // Will be populated when fields are available
        operator: "is_set",
        value: "",
      };
    } else if (operator === "=" && value === false) {
      return {
        id: uuidv4(),
        field,
        fieldObject: null, // Will be populated when fields are available
        operator: "is_not_set",
        value: "",
      };
    }

    // Handle array values for "in" and "not in"
    if ((operator === "in" || operator === "not in") && Array.isArray(value)) {
      return {
        id: uuidv4(),
        field,
        fieldObject: null, // Will be populated when fields are available
        operator: operator as ComparisonType,
        value: value.join(","),
      };
    }

    // Regular case
    return {
      id: uuidv4(),
      field,
      fieldObject: null, // Will be populated when fields are available
      operator: operator as ComparisonType,
      value,
    };
  });
};

const Domain = () => {
  const { fields } = useGraphConfig();
  const { config, setConfig } = useContext(GraphConfigFormContext);
  const [domainRules, setDomainRules] = useState<DomainRule[]>([]);
  const { t } = useTranslation();

  // Helper function to fetch subfields for many2one fields (same as FieldSelector)
  const fetchSubFields = async (field: Field): Promise<Field[]> => {
    const fieldsData = await axios
      .post("/odash/execute", {
        action: "get_model_fields",
        parameters: { model_name: field.model },
      })
      .then((e) => e.data)
      .catch((_e) => null);
    if (fieldsData) {
      const refactoredFields = refactor_fields(fieldsData) as any;
      return refactoredFields;
    }
    return [];
  };

  // Helper function to reconstruct Field object from dot-separated path
  const reconstructFieldObject = async (
    fieldPath: string,
    availableFields: Field[]
  ): Promise<Field | null> => {
    if (!fieldPath) return null;

    const pathParts = fieldPath.split(".");
    let currentFieldList = availableFields;
    let currentField: Field | null = null;

    for (let i = 0; i < pathParts.length; i++) {
      const fieldName = pathParts[i];
      currentField =
        currentFieldList.find((f) => f.field === fieldName) || null;

      if (!currentField) {
        console.warn(`Field '${fieldName}' not found in path '${fieldPath}'`);
        return null;
      }

      // If this is the last part, we found our target field
      if (i === pathParts.length - 1) {
        // Create the final field object with the full path
        return {
          ...currentField,
          field: fieldPath,
          value: fieldPath,
          search: fieldPath,
          label: pathParts
            .map((part, index) => {
              const partField =
                index === 0
                  ? availableFields.find((f) => f.field === part)
                  : null; // For nested parts, we'd need to track this through the recursion
              return partField?.label || partField?.name || part;
            })
            .join(" → "),
        };
      }

      // If this is not the last part and it's a many2one, fetch subfields
      if (i < pathParts.length - 1 && currentField.type === "many2one") {
        try {
          currentFieldList = await fetchSubFields(currentField);
        } catch (error) {
          console.error(
            `Failed to fetch subfields for '${currentField.field}':`,
            error
          );
          return null;
        }
      }
    }

    return currentField;
  };

  // Helper function to populate fieldObject for existing domain rules
  const populateFieldObjects = async (
    rules: DomainRule[],
    availableFields: Field[]
  ): Promise<DomainRule[]> => {
    const updatedRules = await Promise.all(
      rules.map(async (rule) => {
        if (!rule.fieldObject && rule.field) {
          // First try to find simple field (non-nested)
          const simpleField = availableFields.find(
            (f) => f.field === rule.field
          );
          if (simpleField) {
            return { ...rule, fieldObject: simpleField };
          }

          // If not found and contains dots, try to reconstruct from nested path
          if (rule.field.includes(".")) {
            const reconstructedField = await reconstructFieldObject(
              rule.field,
              availableFields
            );
            if (reconstructedField) {
              return { ...rule, fieldObject: reconstructedField };
            }
          }
        }
        return rule;
      })
    );
    return updatedRules;
  };

  // Initialize domainRules from config
  useEffect(() => {
    if (config?.data_source?.domain) {
      const initialRules = convertOdooFormatToDomainRules(
        config.data_source.domain
      );
      setDomainRules(initialRules);
    }
  }, []);

  // Populate fieldObjects when fields become available
  useEffect(() => {
    if (fields.length > 0 && domainRules.length > 0) {
      const populateAsync = async () => {
        const updatedRules = await populateFieldObjects(domainRules, fields);
        const hasChanges = updatedRules.some(
          (rule, index) => rule.fieldObject !== domainRules[index].fieldObject
        );
        if (hasChanges) {
          setDomainRules(updatedRules);
        }
      };
      populateAsync();
    }
  }, [fields, domainRules]);

  // Update config when domainRules change
  const handleDomainRulesChange = (newDomainRules: DomainRule[]) => {
    setDomainRules(newDomainRules);

    // Convert to Odoo format and update config
    const odooFormat = convertDomainRulesToOdooFormat(newDomainRules, fields);
    setConfig({
      ...config,
      data_source: {
        ...config?.data_source,
        domain: odooFormat,
      },
    } as any);
  };

  // Add a new domain rule
  const handleAddDomainRule = () => {
    const newRule: DomainRule = {
      id: uuidv4(),
      field: "",
      fieldObject: null,
      operator: "=",
      value: "",
    };
    handleDomainRulesChange([...domainRules, newRule]);
  };

  // Remove a domain rule
  const handleRemoveDomainRule = (id: string) => {
    handleDomainRulesChange(domainRules.filter((rule) => rule.id !== id));
  };

  // Update a domain rule
  const handleUpdateDomainRule = (id: string, field: string, value: any) => {
    const updatedRules = domainRules.map((rule) => {
      if (rule.id !== id) return rule;

      const updatedRule = { ...rule, [field]: value };

      // Handle field object updates
      if (field === "fieldObject" && value) {
        updatedRule.field = value.field;
        updatedRule.fieldObject = value;
        updatedRule.value = "";
        updatedRule.operator = "=";
      } else if (field === "field") {
        // Legacy string field update - reset value and operator
        updatedRule.value = "";
        updatedRule.operator = "=";
      }

      return updatedRule;
    });

    handleDomainRulesChange(updatedRules);
  };

  // Render appropriate input based on field type
  const renderValueInput = (rule: DomainRule) => {
    // Use the fieldObject if available, otherwise try to find by field path
    const fieldInfo =
      rule.fieldObject || fields.find((f) => f.field === rule.field);

    if (!fieldInfo) return null;

    // Don't show input for special operators
    if (rule.operator === "is_set" || rule.operator === "is_not_set") {
      return (
        <div className="h-10 flex items-center text-sm text-muted-foreground">
          {rule.operator === "is_set"
            ? t("editor.graphConfig.domain.valueIsSet")
            : t("editor.graphConfig.domain.valueIsNotSet")}
        </div>
      );
    }

    // For "in" and "not in" operators with many2one fields, use ManyToManyPicker
    if (
      (rule.operator === "in" || rule.operator === "not in") &&
      (fieldInfo.type === "many2one" ||
        fieldInfo.type === "many2many" ||
        fieldInfo.type === "one2many")
    ) {
      return (
        <ManyToManyPicker
          config={config}
          rule={rule}
          onChange={(value) => handleUpdateDomainRule(rule.id, "value", value)}
        />
      );
    }

    // For "in" and "not in" operators with other field types, provide an input for comma-separated values
    if (rule.operator === "in" || rule.operator === "not in") {
      return (
        <Input
          value={rule.value || ""}
          onChange={(e) => {
            handleUpdateDomainRule(rule.id, "value", e.target.value);
          }}
          placeholder={t("editor.graphConfig.domain.enterCommaSeparatedValues")}
          className="w-full"
        />
      );
    }

    if (["integer", "float"].includes(fieldInfo.type)) {
      return (
        <Input
          type="number"
          value={rule.value || ""}
          onChange={(e) =>
            handleUpdateDomainRule(rule.id, "value", e.target.value)
          }
          placeholder={t("editor.graphConfig.domain.enterValue")}
          className="w-full"
        />
      );
    } else if (
      fieldInfo.type === "many2one" ||
      fieldInfo.type === "many2many" ||
      fieldInfo.type === "one2many"
    ) {
      return (
        <ManyToOnePicker
          config={config}
          rule={rule}
          onChange={(value) => handleUpdateDomainRule(rule.id, "value", value)}
        />
      );
    } else if (fieldInfo.type === "boolean") {
      return (
        <Select
          value={String(rule.value)}
          onValueChange={(value) =>
            handleUpdateDomainRule(rule.id, "value", value === "true")
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={t("editor.graphConfig.domain.selectValue")}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">
              {t("editor.graphConfig.domain.booleanValues.true")}
            </SelectItem>
            <SelectItem value="false">
              {t("editor.graphConfig.domain.booleanValues.false")}
            </SelectItem>
          </SelectContent>
        </Select>
      );
    } else if (fieldInfo.type === "date") {
      return (
        <DomainDatePicker
          includeTime={false}
          rule={rule}
          handleChange={(value) =>
            handleUpdateDomainRule(rule.id, "value", value)
          }
        />
      );
    } else if (fieldInfo.type === "datetime") {
      return (
        <DomainDatePicker
          includeTime={true}
          rule={rule}
          handleChange={(value) =>
            handleUpdateDomainRule(rule.id, "value", value)
          }
        />
      );
    } else if (fieldInfo.type === "selection") {
      return (
        <Select
          value={rule.value || ""}
          onValueChange={(value) =>
            handleUpdateDomainRule(rule.id, "value", value)
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={t("editor.graphConfig.domain.selectValue")}
            />
          </SelectTrigger>
          <SelectContent>
            {fieldInfo.selection?.map(
              (option: { value: string; label: string }) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      );
    } else {
      return (
        <Input
          value={rule.value || ""}
          onChange={(e) =>
            handleUpdateDomainRule(rule.id, "value", e.target.value)
          }
          placeholder={t("editor.graphConfig.domain.enterValue")}
          className="w-full"
        />
      );
    }
  };

  // No need to convert fields - FieldSelector uses Field objects directly

  return (
    <div className="flex flex-col gap-4 w-full px-4 pt-4 pb-[100px]">
      {domainRules.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-6 border border-dashed rounded-md">
          <p className="text-sm text-muted-foreground mb-4">
            {t("editor.graphConfig.domain.noFilters")}
          </p>
          <Button onClick={handleAddDomainRule}>
            {t("editor.graphConfig.domain.addFilter")}
          </Button>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {domainRules.map((rule) => (
              <div
                key={rule.id}
                className="flex flex-row gap-3 p-3 border rounded-md"
              >
                <div className="flex flex-col gap-3 flex-1">
                  <div className="flex-1">
                    <FieldSelector
                      value={rule.fieldObject}
                      fields={fields}
                      placeholder={t("editor.graphConfig.domain.selectField")}
                      noFoundPlaceholder={t(
                        "editor.graphConfig.domain.noFieldFound"
                      )}
                      onChange={(fieldObject) =>
                        handleUpdateDomainRule(
                          rule.id,
                          "fieldObject",
                          fieldObject
                        )
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <Select
                      value={rule.operator}
                      onValueChange={(value) =>
                        handleUpdateDomainRule(
                          rule.id,
                          "operator",
                          value as ComparisonType
                        )
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t(
                            "editor.graphConfig.domain.selectOperator"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {getOperatorMappings(t).map((op) => (
                          <SelectItem key={op.value} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">{renderValueInput(rule)}</div>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleRemoveDomainRule(rule.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={handleAddDomainRule}
            className="w-full"
          >
            {t("editor.graphConfig.domain.addAnotherFilter")}
          </Button>

          {domainRules.length > 0 && (
            <div className="mt-4 p-3 border rounded-md bg-muted/50">
              <Label className="mb-2 block">
                {t("editor.graphConfig.domain.previewDomain")}
              </Label>
              <code className="text-xs block whitespace-pre-wrap">
                {JSON.stringify(
                  convertDomainRulesToOdooFormat(domainRules, fields),
                  null,
                  2
                )}
              </code>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Domain;
